import type { CompilationResult } from "@datapolarna/formulaparser";
import type { ShapeStyle } from "shared/mapStyles/domain/shapeStyle";
import type { VehicleStyle } from "shared/mapStyles/domain/vehicleStyle";
import type { PlaceStyle } from "shared/mapStyles/domain/placeStyle";
import type { PlaceBackgroundPattern, PlaceContext, PlaceTextAlignment, PlaceTextPosition } from "shared/mapStyles/domain/placeContext";
import type { PackageStyle } from "shared/mapStyles/domain/packageStyle";
import type { PackageContext } from "shared/mapStyles/domain/packageContext";
import type { ProximityStyle } from "shared/mapStyles/domain/proximityStyle";
import type { ProximityContext } from "shared/mapStyles/domain/proximityContext";
import type { MapStyle } from "shared/mapStyles/domain/mapStyle";
import { language as shapeLanguage } from 'shared/mapStyles/domain/shapeFormulaLanguage';
import { language as vehicleLanguage } from 'shared/mapStyles/domain/vehicleFormulaLanguage';
import { language as placeLanguage } from 'shared/mapStyles/domain/placeFormulaLanguage';
import { language as packageLanguage } from 'shared/mapStyles/domain/packageFormulaLanguage';
import { language as proximityLanguage } from 'shared/mapStyles/domain/proximityFormulaLanguage';
import type { MapStyleResponse } from "../api/mapStyleResponse";

export interface CreateMapStyleOptions {
  style: MapStyleResponse;
}

export interface CreateMapStyleResult {
  mapStyle: MapStyle;
  compilation: CreateMapStyleCompilationResult;
}

export interface CreateMapStyleCompilationResult {
  shapeColor?: CompilationResult;
  shapeLabel?: CompilationResult;
  shapeOutlineColor?: CompilationResult;
  shapeOutlineSize?: CompilationResult;
  vehicleColor?: CompilationResult;
  shapeFontSize?: CompilationResult;
  shapeMessageIcon?: CompilationResult;
}

export function createMapStyle(options: CreateMapStyleOptions) : CreateMapStyleResult {

  const {
    style,
  } = options;

  const {
    shapeColorFormula,
    shapeLabelFormula,
    shapeOutlineColorFormula,
    shapeOutlineSizeFormula,
    vehicleColorFormula,
    shapeFontSizeFormula,
    shapeMessageIconFormula,
    placeVisibleFormula,
    placeLabelFormula,
    placeOutlineColorFormula,
    placeOutlineSizeFormula,
    placeAccessibleOutlineColorFormula,
    placeInaccessibleOutlineColorFormula,
    placeTextAlignmentFormula,
    placeTextPositionFormula,
    placeTextOpacityFormula,
    placeTextColorFormula,
    placeTextSizeFormula,
    placeBackgroundColorFormula,
    placeBackgroundPatternFormula,
    placeBackgroundOpacityFormula,
    packageVisibleFormula,
    packageLabelFormula,
    packageOutlineColorFormula,
    packageOutlineSizeFormula,
    packageTextAlignmentFormula,
    packageTextPositionFormula,
    packageTextOpacityFormula,
    packageTextColorFormula,
    packageTextSizeFormula,
    packageBackgroundColorFormula,
    packageBackgroundPatternFormula,
    packageBackgroundOpacityFormula,
    proximityVisibleFormula,
    proximityAreaColorFormula,
    proximityConeColorFormula,
  } = style;

  const compilation: CreateMapStyleCompilationResult = {};


  if (shapeColorFormula) {
    compilation.shapeColor = shapeLanguage.formulaParser.compile(shapeColorFormula);
  }

  if (shapeLabelFormula) {
    compilation.shapeLabel = shapeLanguage.formulaParser.compile(shapeLabelFormula);
  }

  if (shapeOutlineColorFormula) {
    compilation.shapeOutlineColor = shapeLanguage.formulaParser.compile(shapeOutlineColorFormula);
  }

  if (shapeOutlineSizeFormula) {
    compilation.shapeOutlineSize = shapeLanguage.formulaParser.compile(shapeOutlineSizeFormula);
  }

  if (vehicleColorFormula) {
    compilation.vehicleColor = vehicleLanguage.formulaParser.compile(vehicleColorFormula);
  }

  if (shapeFontSizeFormula) {
    compilation.shapeFontSize = shapeLanguage.formulaParser.compile(shapeFontSizeFormula);
  }

  if (shapeMessageIconFormula) {
    compilation.shapeMessageIcon = shapeLanguage.formulaParser.compile(shapeMessageIconFormula);
  }

  const shapeStyle: ShapeStyle = {
    color(context) {
      if (shapeColorFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeColorFormula, context);
        if (!result.compilation.success) {
          return '#ffffff';
        }
        return result.text.length > 0 ? result.text : '#ffffff';
      }
      return '#ffffff';
    },
    label(context) {
      if (shapeLabelFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeLabelFormula, context);
        if (!result.compilation.success) {
          return '';
        }
        return result.text;
      }
      return '';
    },
    outlineColor(context) {
      if (shapeOutlineColorFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeOutlineColorFormula, context);
        if (!result.compilation.success) {
          return '#000000';
        }
        return result.text;
      }
      return '#000000';
    },
    outlineSize(context) {
      if (shapeOutlineSizeFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeOutlineSizeFormula, context);
        if (!result.compilation.success) {
          return 1;
        }

        const size = parseFloat(result.text);
        return size;
      }
      return 1;
    },
    fontSize(context) {
      if (shapeFontSizeFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeFontSizeFormula, context);
        if (!result.compilation.success) {
          return 12;
        }

        const size = parseFloat(result.text);
        return size;
      }
      return 12;
    },
    messageIconVisible(context) {

      if (shapeMessageIconFormula) {
        const result = shapeLanguage.formulaParser.evaluate(shapeMessageIconFormula, context);
        if (result.compilation.success) {
          if (result.text && result.text !== 'false' && result.text !== '0') {
            return true;
          } else {
            return false;
          }
        }
      }

      const note = context.storageArea?.note;
      return note !== undefined && note !== '';
    },
  };

  const vehicleStyle: VehicleStyle = {
    color(context) {
      if (vehicleColorFormula) {
        const result = vehicleLanguage.formulaParser.evaluate(vehicleColorFormula, context);
        if (!result.compilation.success) {
          return '#000000';
        }
        return result.text;
      }

      return context.color ?? '#000000';
    },
  };

  function evaluatePlaceText(formula: string | undefined, context: PlaceContext): string | null {
    if (!formula) return null;
    const result = placeLanguage.formulaParser.evaluate(formula, context);
    if (!result.compilation.success) return null;
    return result.text;
  }

  function evaluatePackageText(formula: string | undefined, context: PackageContext): string | null {
    if (!formula) return null;
    const result = packageLanguage.formulaParser.evaluate(formula, context);
    if (!result.compilation.success) return null;
    return result.text;
  }

  function evaluateProximityText(formula: string | undefined, context: ProximityContext): string | null {
    if (!formula) return null;
    const result = proximityLanguage.formulaParser.evaluate(formula, context);
    if (!result.compilation.success) return null;
    return result.text;
  }

  const placeStyle: PlaceStyle = {
    visible(context) {
      const text = evaluatePlaceText(placeVisibleFormula, context);
      if (text === null) return true;
      return text !== '' && text !== 'false' && text !== '0';
    },
    label(context) {
      return evaluatePlaceText(placeLabelFormula, context) ?? '';
    },
    outlineColor(context) {
      const text = evaluatePlaceText(placeOutlineColorFormula, context);
      return text && text.length > 0 ? text : '#000000';
    },
    outlineSize(context) {
      const text = evaluatePlaceText(placeOutlineSizeFormula, context);
      if (text === null) return 1;
      const size = parseFloat(text);
      return Number.isNaN(size) ? 1 : size;
    },
    accessibleOutlineColor(context) {
      const text = evaluatePlaceText(placeAccessibleOutlineColorFormula, context);
      return text && text.length > 0 ? text : null;
    },
    inaccessibleOutlineColor(context) {
      const text = evaluatePlaceText(placeInaccessibleOutlineColorFormula, context);
      return text && text.length > 0 ? text : null;
    },
    textAlignment(context) {
      const text = evaluatePlaceText(placeTextAlignmentFormula, context);
      if (text === 'left' || text === 'center' || text === 'right') {
        return text as PlaceTextAlignment;
      }
      return 'center';
    },
    textPosition(context) {
      const text = evaluatePlaceText(placeTextPositionFormula, context);
      if (text === 'center' || text === 'top' || text === 'bottom') {
        return text as PlaceTextPosition;
      }
      return 'center';
    },
    textOpacity(context) {
      const text = evaluatePlaceText(placeTextOpacityFormula, context);
      if (text === null) return 1;
      const opacity = parseFloat(text);
      return Number.isNaN(opacity) ? 1 : Math.min(Math.max(opacity / 100, 0), 1);
    },
    textColor(context) {
      const text = evaluatePlaceText(placeTextColorFormula, context);
      return text && text.length > 0 ? text : '#000000';
    },
    textSize(context) {
      const text = evaluatePlaceText(placeTextSizeFormula, context);
      if (text === null) return 12;
      const size = parseFloat(text);
      return Number.isNaN(size) ? 12 : size;
    },
    backgroundColor(context) {
      const text = evaluatePlaceText(placeBackgroundColorFormula, context);
      return text && text.length > 0 ? text : '#ffffff';
    },
    backgroundPattern(context) {
      const text = evaluatePlaceText(placeBackgroundPatternFormula, context);
      if (text === 'solid' || text === 'hatch' || text === 'cross' || text === 'dots') {
        return text as PlaceBackgroundPattern;
      }
      return 'solid';
    },
    backgroundOpacity(context) {
      const text = evaluatePlaceText(placeBackgroundOpacityFormula, context);
      if (text === null) return 1;
      const opacity = parseFloat(text);
      return Number.isNaN(opacity) ? 1 : Math.min(Math.max(opacity / 100, 0), 1);
    },
  };

  const packageStyle: PackageStyle = {
    visible(context) {
      const text = evaluatePackageText(packageVisibleFormula, context);
      if (text === null) return true;
      return text !== '' && text !== 'false' && text !== '0';
    },
    label(context) {
      return evaluatePackageText(packageLabelFormula, context) ?? '';
    },
    outlineColor(context) {
      const text = evaluatePackageText(packageOutlineColorFormula, context);
      return text && text.length > 0 ? text : '#000000';
    },
    outlineSize(context) {
      const text = evaluatePackageText(packageOutlineSizeFormula, context);
      if (text === null) return 1;
      const size = parseFloat(text);
      return Number.isNaN(size) ? 1 : size;
    },
    textAlignment(context) {
      const text = evaluatePackageText(packageTextAlignmentFormula, context);
      if (text === 'left' || text === 'center' || text === 'right') {
        return text as PlaceTextAlignment;
      }
      return 'center';
    },
    textPosition(context) {
      const text = evaluatePackageText(packageTextPositionFormula, context);
      if (text === 'center' || text === 'top' || text === 'bottom') {
        return text as PlaceTextPosition;
      }
      return 'center';
    },
    textOpacity(context) {
      const text = evaluatePackageText(packageTextOpacityFormula, context);
      if (text === null) return 1;
      const opacity = parseFloat(text);
      return Number.isNaN(opacity) ? 1 : Math.min(Math.max(opacity / 100, 0), 1);
    },
    textColor(context) {
      const text = evaluatePackageText(packageTextColorFormula, context);
      return text && text.length > 0 ? text : '#000000';
    },
    textSize(context) {
      const text = evaluatePackageText(packageTextSizeFormula, context);
      if (text === null) return 12;
      const size = parseFloat(text);
      return Number.isNaN(size) ? 12 : size;
    },
    backgroundColor(context) {
      const text = evaluatePackageText(packageBackgroundColorFormula, context);
      return text && text.length > 0 ? text : '#ffffff';
    },
    backgroundPattern(context) {
      const text = evaluatePackageText(packageBackgroundPatternFormula, context);
      if (text === 'solid' || text === 'hatch' || text === 'cross' || text === 'dots') {
        return text as PlaceBackgroundPattern;
      }
      return 'solid';
    },
    backgroundOpacity(context) {
      const text = evaluatePackageText(packageBackgroundOpacityFormula, context);
      if (text === null) return 1;
      const opacity = parseFloat(text);
      return Number.isNaN(opacity) ? 1 : Math.min(Math.max(opacity / 100, 0), 1);
    },
  };

  const proximityStyle: ProximityStyle = {
    visible(context) {
      const text = evaluateProximityText(proximityVisibleFormula, context);
      if (text === null) return false;
      return text !== '' && text !== 'false' && text !== '0';
    },
    areaColor(context) {
      const text = evaluateProximityText(proximityAreaColorFormula, context);
      return text && text.length > 0 ? text : '#2563eb';
    },
    coneColor(context) {
      const text = evaluateProximityText(proximityConeColorFormula, context);
      return text && text.length > 0 ? text : '#22c55e';
    },
  };

  const mapStyle: MapStyle = {
    id: style.id,
    name: style.name,
    shape: shapeStyle,
    vehicle: vehicleStyle,
    place: placeStyle,
    package: packageStyle,
    proximity: proximityStyle,
  };

  return {
    mapStyle,
    compilation
  };
}
